import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Check, Mail, MessageSquare, Phone } from "lucide-react";
import { OPEN_PROBLEMS } from "../home/openProblems";
import { audienceFromPath } from "../audience/audienceSites";

const solutionGroups = [
  {
    label: "Help choosing",
    options: [
      { value: "workflow-review", label: "Help me choose the right starting point" },
      { value: "workflow-assessment", label: "Start with a workflow assessment" },
    ],
  },
  {
    label: "Assays and methods",
    options: [
      { value: "assay-automation", label: "Automate a working plate assay" },
      { value: "method-development", label: "Add method-development capacity" },
      { value: "bespoke-assay-development", label: "Develop a plate assay for automation" },
      { value: "liquid-class-development", label: "Tune a difficult liquid transfer" },
      { value: "method-transfer", label: "Transfer a method to another instrument or site" },
      { value: "automation-rescue", label: "Rescue or optimize existing automation" },
      { value: "cell-based-assay-automation", label: "Automate a cell-based assay" },
      { value: "ipsc-differentiation", label: "Coordinate an iPSC differentiation workflow" },
      { value: "sirna-screening", label: "Build a repeatable siRNA screen" },
      { value: "antibody-screening", label: "Automate an antibody screen" },
    ],
  },
  {
    label: "Automation and integrations",
    options: [
      { value: "workcell-design-install", label: "Design and install an on-prem workcell" },
      { value: "workcell-integration", label: "Integrate instruments into a workcell" },
      { value: "governed-orchestration", label: "Co-design workflow orchestration" },
    ],
  },
  {
    label: "Scientific data",
    options: [
      { value: "cro-data-harmonization", label: "Harmonize recurring CRO data and reports" },
      { value: "scientific-dataset-engineering", label: "Build a reusable scientific dataset" },
      { value: "data-automation", label: "Automate an ELN, LIMS, or scientific data flow" },
    ],
  },
  {
    label: "Scientific AI and agent infrastructure",
    options: [
      { value: "private-evaluation-dataset", label: "Build a private scientific evaluation set" },
      { value: "agent-evaluation-harness", label: "Build scientific AI evaluations and release gates" },
      { value: "model-training-datasets", label: "Build a model-training dataset" },
      { value: "agent-runtime-infrastructure", label: "Connect agents to lab hardware" },
      { value: "scientific-ai-guardrails", label: "Add execution controls and approval gates" },
      { value: "agent-tracing-observability", label: "Add agent tracing and observability" },
      { value: "supervised-agent-orchestration", label: "Pilot a scientist-supervised agent workflow" },
      { value: "lab-mcp-development", label: "Build an MCP integration for a lab system" },
      { value: "workflow-code-recovery", label: "Fix or recover vibe-coded lab software" },
      { value: "method-ide", label: "Co-design a method review workspace" },
      { value: "automation-sdk", label: "Co-design lab SDKs or APIs" },
    ],
  },
];

const specificSolutionOptions = solutionGroups.flatMap(({ options }) => options);

const solutionToInterest = {
  "workflow-review": "not-sure",
  "workflow-assessment": "not-sure",
  "assay-automation": "methods",
  "method-development": "methods",
  "method-transfer": "methods",
  "automation-rescue": "methods",
  "method-integration-delivery": "methods",
  "liquid-class-development": "assays",
  "bespoke-assay-development": "assays",
  "cell-based-assay-automation": "assays",
  "ipsc-differentiation": "assays",
  "sirna-screening": "assays",
  "antibody-screening": "assays",
  "workcell-design-install": "integrations",
  "workcell-integration": "integrations",
  "governed-orchestration": "integrations",
  "data-automation": "data",
  "cro-data-harmonization": "data",
  "scientific-dataset-engineering": "data",
  "private-evaluation-dataset": "scientific-ai",
  "agent-evaluation-harness": "scientific-ai",
  "model-training-datasets": "scientific-ai",
  "agent-runtime-infrastructure": "scientific-ai",
  "scientific-ai-guardrails": "scientific-ai",
  "agent-tracing-observability": "scientific-ai",
  "supervised-agent-orchestration": "scientific-ai",
  "lab-mcp-development": "scientific-ai",
  "workflow-code-recovery": "scientific-ai",
  "method-ide": "scientific-ai",
  "automation-sdk": "scientific-ai",
  "software-enterprise-access": "scientific-ai",
};

const interestLabels = {
  "not-sure": "Help choosing",
  methods: "Method development, transfer, or rescue",
  assays: "Assay or liquid-class development",
  integrations: "Instrument or workcell integration",
  data: "Scientific data workflow",
  "scientific-ai": "Scientific AI and agent infrastructure",
};

const legacySolutionAliases = {
  "method-integration-delivery": "workflow-review",
  "software-enterprise-access": "workflow-review",
};

const engagementLabels = {
  "focused-installation": "Focused build & handoff",
  "managed-integration": "Managed integration",
  "embedded-fde": "Embedded forward-deployed engineering",
};

const validSolutionIds = new Set(specificSolutionOptions.map(({ value }) => value));

const audienceInquiries = {
  treatment: {
    eyebrow: "Research conversation",
    title: "Talk with our team.",
    introduction: "We’d like to hear from patients, families and advocates.",
    prefill: "",
    contextLabel: "Your message",
    placeholder: "A short, general introduction is enough.",
    help: "This is a research conversation, not medical care or access to treatment. Do not send medical records, genetic files, or personal health details.",
    boundary: "Your message and contact details go to Cornucopia’s CRM so our team can respond.",
    submitLabel: "Send research inquiry",
    successTitle: "Thank you for reaching out.",
    successCopy: "Your research inquiry is with our team.",
  },
  investor: {
    eyebrow: "Investment conversation",
    title: "Let’s talk about Cornucopia.",
    introduction: "Tell us what caught your interest, and what you’d like to know.",
    prefill: "I’d like to learn more about Cornucopia.",
    contextLabel: "What would you like to discuss?",
    placeholder: "A little about your investment focus and what you’d like to discuss.",
    help: "Please keep this introduction nonconfidential.",
    boundary: "Your message and contact details go to Cornucopia’s CRM so our team can respond.",
    submitLabel: "Send investment inquiry",
    successTitle: "Thanks for getting in touch.",
    successCopy: "We’ve received your message.",
  },
};

const ContactUs = () => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const requestedAudience = audienceFromPath(pathname) || searchParams.get("audience");
  const requestedSource = searchParams.get("source");
  const audience = Object.prototype.hasOwnProperty.call(audienceInquiries, requestedAudience)
    ? requestedAudience
    : !requestedAudience && requestedSource === "investor-story"
      ? "investor"
      : !requestedAudience && requestedSource === "treatment-story"
        ? "treatment"
        : null;
  const audienceInquiry = audienceInquiries[audience];
  const requestedSolution = searchParams.get("solution");
  const normalizedRequestedSolution =
    legacySolutionAliases[requestedSolution] || requestedSolution;
  const initialSolution = validSolutionIds.has(normalizedRequestedSolution)
    ? normalizedRequestedSolution
    : "workflow-review";
  const requestedEngagement = searchParams.get("engagement");
  const selectedEngagement = !audienceInquiry && engagementLabels[requestedEngagement]
    ? requestedEngagement
    : null;
  const selectedProblem = !audienceInquiry && OPEN_PROBLEMS.find(
    ({ id }) => id === searchParams.get("problem")
  );
  const inquiryPrefill = audienceInquiry?.prefill || (selectedProblem
    ? `I’d like to discuss ${selectedProblem.title}.`
    : "");
  const previousInquiryPrefill = useRef(inquiryPrefill);
  const source = requestedSource ||
    (selectedProblem ? "request-for-problems" : "direct");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    solution: initialSolution,
    currentSystem: "",
    message: inquiryPrefill,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, margin: "-10% 0px" });
  const reduced = useReducedMotion();
  const selectedSpecificSolution = useMemo(
    () => specificSolutionOptions.find(({ value }) => value === formData.solution),
    [formData.solution]
  );
  const selectedInterest = selectedProblem && formData.solution === "workflow-review"
    ? selectedProblem.interest
    : solutionToInterest[formData.solution] || "not-sure";
  const cameFromSolution = Boolean(
    requestedSolution &&
      validSolutionIds.has(normalizedRequestedSolution) &&
      normalizedRequestedSolution !== "workflow-review"
  );

  useEffect(() => {
    const previousPrefill = previousInquiryPrefill.current;
    setFormData((current) => ({
      ...current,
      solution: initialSolution,
      message: inquiryPrefill !== previousPrefill &&
        (!current.message || current.message === previousPrefill)
        ? inquiryPrefill
        : current.message,
    }));
    previousInquiryPrefill.current = inquiryPrefill;
    setIsSubmitted(false);
    setError(null);
  }, [initialSolution, inquiryPrefill]);

  const handleChange = (event) => {
    const { id, value } = event.target;
    setFormData((current) => ({
      ...current,
      [id]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const portalId = "48423791";
    const formId = "4d5bd9a7-d69c-45da-a777-ce0122f99e4a";
    const endpoint = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;
    const contextualMessage = (audienceInquiry ? [
      `Website audience: ${audience}`,
      `Inquiry type: ${audienceInquiry.eyebrow}`,
      `Organization: ${formData.company || "Not provided"}`,
      `Website source: ${source}`,
      formData.message ? `${audienceInquiry.contextLabel}:\n${formData.message}` : null,
    ] : [
      selectedProblem ? `Open problem: ${selectedProblem.title}` : null,
      `Problem area: ${interestLabels[selectedInterest] || selectedInterest}`,
      `Requested solution: ${selectedSpecificSolution?.label || "Not specified"}`,
      `Requested solution ID: ${formData.solution}`,
      `Original website selection: ${requestedSolution || "Not specified"}`,
      `Preferred engagement model: ${
        selectedEngagement
          ? engagementLabels[selectedEngagement]
          : "Not specified"
      }`,
      `Company / lab: ${formData.company}`,
      `Current instruments, systems, or data sources: ${formData.currentSystem || "Not provided"}`,
      `Website source: ${source}`,
      formData.message ? `Starting-point details:\n${formData.message}` : null,
    ])
      .filter(Boolean)
      .join("\n\n");

    const data = {
      fields: [
        { name: "firstname", value: formData.firstName },
        { name: "lastname", value: formData.lastName },
        { name: "email", value: formData.email },
        { name: "company", value: formData.company },
        { name: "message", value: contextualMessage },
      ],
      context: {
        pageUri: window.location.href,
        pageName: document.title,
      },
    };

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HubSpot returned ${response.status}`);
      }

      setIsSubmitted(true);
    } catch (submissionError) {
      console.error("Form submission error", submissionError);
      setError(
        "We could not send this request. Email hello@cornucopiabio.com and we will pick it up there."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeUp = (delay = 0) =>
    reduced
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          animate: headerInView ? { opacity: 1, y: 0 } : {},
          transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1], delay },
        };

  const inputClass =
    "w-full min-h-12 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-base text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 dark:border-white/[0.08] dark:bg-white/[0.035] dark:text-white dark:placeholder:text-white/60 sm:text-sm";
  const labelClass =
    "mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-500 dark:text-white/70";

  return (
    <main className="bg-white px-6 py-28 text-gray-900 dark:bg-[#050507] dark:text-white sm:py-36">
      <div className="wide-shell-lg mx-auto grid max-w-6xl gap-14 lg:grid-cols-[0.7fr_1.3fr] lg:gap-20">
        <div ref={headerRef}>
          <motion.h1 {...fadeUp(0.06)} className="mb-6 font-serif text-5xl font-normal leading-[1.04] tracking-tight sm:text-6xl">
            {audienceInquiry?.title || (selectedProblem
              ? "Let’s work on it together."
              : "What are you working on?")}
          </motion.h1>
          {(audienceInquiry || selectedProblem) && <motion.p {...fadeUp(0.12)} className="mb-9 text-lg leading-8 text-gray-600 dark:text-white/[0.65]">
            {audienceInquiry?.introduction || "Tell us what you’re exploring and where we could help."}
          </motion.p>}

          {!audienceInquiry && <motion.div {...fadeUp(0.18)} className="mb-10 hidden rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-white/[0.08] dark:bg-white/[0.03] lg:block">
            <p className="mb-4 text-sm font-semibold">What helps us understand it</p>
            <ul className="space-y-3">
              {["Your current workflow", "The systems involved", "What a useful result would look like"].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm leading-6 text-gray-600 dark:text-white/[0.65]">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 border-t border-gray-200 pt-4 text-xs leading-5 text-gray-500 dark:border-white/[0.08] dark:text-white/50">
              We’ll agree on a secure way to share files or system access if needed.
            </p>
          </motion.div>}

          <motion.div {...fadeUp(0.22)} className="hidden space-y-3 text-sm lg:block">
            <a href="mailto:hello@cornucopiabio.com" className="flex min-h-11 items-center gap-3 rounded-lg text-gray-600 outline-none transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-primary dark:text-white/70">
              <Mail className="h-4 w-4" aria-hidden="true" />
              hello@cornucopiabio.com
            </a>
            <a href="https://calendly.com/hello-cornucopiabio/cornucopia-consultation-call" target="_blank" rel="noopener noreferrer" className="flex min-h-11 items-center gap-3 rounded-lg text-gray-600 outline-none transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-primary dark:text-white/70">
              <Phone className="h-4 w-4" aria-hidden="true" />
              {audienceInquiry ? "Prefer to talk? Book a conversation" : "Prefer to talk? Book a project discussion"}
            </a>
          </motion.div>
        </div>

        <motion.div {...fadeUp(0.16)}>
          {isSubmitted ? (
            <div role="status" className="rounded-2xl border border-gray-200 bg-gray-50 p-10 text-center dark:border-white/[0.08] dark:bg-white/[0.03] sm:p-12">
              <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10">
                <MessageSquare className="h-6 w-6 text-emerald-500" aria-hidden="true" />
              </div>
              <h2 className="mb-3 text-2xl font-bold">{audienceInquiry?.successTitle || "Project inquiry received."}</h2>
              <p className="text-sm leading-6 text-gray-600 dark:text-white/[0.65]">
                {audienceInquiry?.successCopy || "Your project inquiry has been received. We will review it and reply with the next useful step."}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5 rounded-lg border border-gray-200 bg-white p-6 dark:border-white/[0.08] dark:bg-white/[0.02] sm:p-8">
              {selectedProblem && (
                <div className="border-l-2 border-primary bg-primary/[0.035] px-4 py-3 dark:bg-primary/[0.06]">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                    Open problem
                  </p>
                  <p className="mt-1.5 text-sm font-semibold text-gray-900 dark:text-white">
                    {selectedProblem.title}
                  </p>
                </div>
              )}
              {selectedEngagement && (
                <div className="border-l-2 border-primary bg-primary/[0.035] px-4 py-3 dark:bg-primary/[0.06]">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                    Preferred working model
                  </p>
                  <p className="mt-1.5 text-sm font-semibold text-gray-900 dark:text-white">
                    {engagementLabels[selectedEngagement]}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-white/60">
                    We will confirm the right ownership model during the workflow assessment.
                  </p>
                </div>
              )}


              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="firstName" className={labelClass}>First name</label>
                  <input id="firstName" type="text" autoComplete="given-name" value={formData.firstName} onChange={handleChange} required className={inputClass} />
                </div>
                <div>
                  <label htmlFor="lastName" className={labelClass}>Last name</label>
                  <input id="lastName" type="text" autoComplete="family-name" value={formData.lastName} onChange={handleChange} required className={inputClass} />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="email" className={labelClass}>{audienceInquiry ? "Email" : "Work email"}</label>
                  <input id="email" type="email" autoComplete="email" value={formData.email} onChange={handleChange} required className={inputClass} />
                </div>
                <div>
                  <label htmlFor="company" className={labelClass}>{audienceInquiry ? "Organization (optional)" : "Company or lab"}</label>
                  <input id="company" type="text" autoComplete="organization" value={formData.company} onChange={handleChange} required={!audienceInquiry} className={inputClass} />
                </div>
              </div>

              {!audienceInquiry && <div>
                <label htmlFor="currentSystem" className={labelClass}>Systems involved <span className="normal-case tracking-normal">(optional)</span></label>
                <input id="currentSystem" type="text" value={formData.currentSystem} onChange={handleChange} placeholder={selectedProblem ? "Datasets, models, assays, or tools involved" : "Hamilton STAR / VENUS 6; plate reader; recurring CSV and PDF deliveries"} className={inputClass} />
                <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-white/50">
                  Add model names or software versions if you know them.
                </p>
              </div>}

              <div>
                <label htmlFor="message" className={labelClass}>{audienceInquiry?.contextLabel || "What decision are you trying to make?"}</label>
                <textarea id="message" value={formData.message} onChange={handleChange} rows={7} required aria-describedby="message-help inquiry-boundary" placeholder={audienceInquiry?.placeholder || "An uncertain result, a stalled assay, or a workflow you want to improve."} className={`${inputClass} resize-y`} />
                <p id="message-help" className="mt-2 text-xs leading-5 text-gray-500 dark:text-white/50">
                  {audienceInquiry?.help || "Leave out confidential or sensitive details."}
                </p>
              </div>

              {!audienceInquiry && <div>
                <label htmlFor="solution" className={labelClass}>What would you like help with?</label>
                <select
                  id="solution"
                  value={formData.solution}
                  onChange={handleChange}
                  required
                  className={`${inputClass} cursor-pointer`}
                >
                  {solutionGroups.map(({ label, options }) => (
                    <optgroup key={label} label={label}>
                      {options.map(({ value, label: optionLabel }) => (
                        <option key={value} value={value}>{optionLabel}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-white/50">
                  {cameFromSolution
                    ? "Selected from the solution you viewed. You can change it."
                    : selectedProblem
                      ? "Your research topic is already included."
                      : "Choose the closest fit, or select help choosing."}
                </p>
                <input type="hidden" name="originalSolution" value={requestedSolution || ""} />
                <input type="hidden" name="engagementModel" value={selectedEngagement || ""} />
              </div>}

              <button
                type="submit"
                disabled={isSubmitting}
                data-solution-id={audienceInquiry ? undefined : formData.solution}
                data-audience={audience || undefined}
                data-problem-id={selectedProblem?.id}
                data-engagement-tier={selectedEngagement || undefined}
                data-cta={audienceInquiry ? `send-${audience}-inquiry` : "send-project-inquiry"}
                className="min-h-12 w-full cursor-pointer rounded-md bg-primary px-6 py-3 text-sm font-semibold text-white outline-none transition-colors hover:bg-primary-600 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-gray-950 dark:hover:bg-white/90 dark:focus-visible:ring-white dark:focus-visible:ring-offset-[#050507]"
              >
                {isSubmitting ? "Sending…" : audienceInquiry?.submitLabel || "Send project inquiry"}
              </button>

              <p id="inquiry-boundary" className="text-xs leading-5 text-gray-500 dark:text-white/45">
                {audienceInquiry?.boundary || "This form sends your information to Cornucopia's CRM so the team can respond. Do not include confidential, patient, or sensitive data."}
              </p>

              {error && <p role="alert" className="text-sm leading-6 text-red-600 dark:text-red-400">{error}</p>}
            </form>
          )}

          <div className="mt-6 space-y-3 text-sm lg:hidden">
            <a href="mailto:hello@cornucopiabio.com" className="flex min-h-11 items-center gap-3 rounded-lg text-gray-600 outline-none transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-primary dark:text-white/70">
              <Mail className="h-4 w-4" aria-hidden="true" />
              hello@cornucopiabio.com
            </a>
            <a href="https://calendly.com/hello-cornucopiabio/cornucopia-consultation-call" target="_blank" rel="noopener noreferrer" className="flex min-h-11 items-center gap-3 rounded-lg text-gray-600 outline-none transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-primary dark:text-white/70">
              <Phone className="h-4 w-4" aria-hidden="true" />
              {audienceInquiry ? "Prefer to talk? Book a conversation" : "Prefer to talk? Book a project discussion"}
            </a>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default ContactUs;
